// @ts-strict-ignore
import { atom, selector } from 'recoil'

export interface UpdateStatuses<T> {
    'in progress': T
    'request finished': T
    success: T
    failure: T
    created: T
    total: T
    'error rate': T
}

/**
 * When enabled, the most recent day's data is shown. This can sometimes
 * not be desired, as the current day's data will likely have some updates
 * that are currently in progress
 */
export const useLastDayAtom = atom<boolean>({
    key: 'updates_show_last_day',
    default: true,
})

export const activeAtom = atom<keyof UpdateStatuses<any>>({
    key: 'update_counts_active_status',
    default: 'success',
})

// Keyed by timestamps
export const rawCounts = atom<{ [key: number]: UpdateStatuses<number> }>({
    key: 'updates_raw_counts',
    default: {},
})

export const timestampsSelector = selector<number[]>({
    key: 'updates_timestamps',
    get: ({ get }) => {
        const res = get(rawCounts)
        const showLastDay = get(useLastDayAtom)

        let ts = Object.keys(res).map((x) => parseInt(x))
        if (!showLastDay) {
            ts = ts.slice(0, ts.length - 1)
        }

        return ts
    },
})

export const totalsSelector = selector<UpdateStatuses<number>>({
    key: 'updates_totals',
    get: ({ get }) => {
        const res = get(rawCounts)

        const totals: UpdateStatuses<number> = {
            'in progress': 0,
            'request finished': 0,
            success: 0,
            failure: 0,
            created: 0,
            total: 0,
            'error rate': 0,
        }

        Object.values(res).forEach((v) => {
            totals['success'] += v.success
            totals['failure'] += v.failure
            totals['created'] += v.created
            totals['in progress'] += v['in progress']
            totals['request finished'] += v['request finished']
            totals['total'] +=
                v.success + v.failure + v.created + v['in progress'] + v['request finished']
        })
        totals['error rate'] = get_rate(totals)

        return totals
    },
})

export const valuesSelector = selector<UpdateStatuses<number[]>>({
    key: 'updates_values',
    get: ({ get }) => {
        const res = get(rawCounts)
        const showLastDay = get(useLastDayAtom)

        const values: UpdateStatuses<number[]> = {
            'in progress': [],
            'request finished': [],
            success: [],
            failure: [],
            created: [],
            total: [],
            'error rate': [],
        }

        Object.values(res).forEach((v) => {
            values['success'].push(v.success)
            values['failure'].push(v.failure)
            values['created'].push(v.created)
            values['in progress'].push(v['in progress'])
            values['request finished'].push(v['request finished'])

            values['total'].push(
                v.success + v.failure + v.created + v['in progress'] + v['request finished'],
            )
            values['error rate'].push(get_rate(v))
        })

        if (!showLastDay) {
            values['success'] = values['success'].slice(0, values['success'].length - 1)
            values['failure'] = values['failure'].slice(0, values['failure'].length - 1)
            values['created'] = values['created'].slice(0, values['created'].length - 1)
            values['in progress'] = values['in progress'].slice(0, values['in progress'].length - 1)
            values['request finished'] = values['request finished'].slice(
                0,
                values['request finished'].length - 1,
            )
            values['total'] = values['total'].slice(0, values['total'].length - 1)
            values['error rate'] = values['error rate'].slice(0, values['error rate'].length - 1)
        }

        return values
    },
})

export const datasetSelector = selector<number[][]>({
    key: 'updates_dataset',
    get: ({ get }) => {
        const active = get(activeAtom)
        const timestamps = get(timestampsSelector)
        const values = get(valuesSelector)

        return [timestamps, values[active], [], []]
    },
})

export const errorRateSelector = selector<number[][]>({
    key: 'updates_error_rate',
    get: ({ get }) => {
        const timestamps = get(timestampsSelector)
        const values = get(valuesSelector)

        return [timestamps, values['error rate'], [], []]
    },
})

export interface AggregatesI {
    total: number
    errors: number
    error_rate: number
    success_rate: number
}

export const aggregates7D = selector<AggregatesI>({
    key: 'updates_7d_aggregates',
    get: ({ get }) => {
        get(timestampsSelector)
        const values = get(valuesSelector)

        const success = values['success'].slice(-7).reduce((a, b) => a + b, 0)
        const total = values['total'].slice(-7).reduce((a, b) => a + b, 0)

        return {
            total,
            errors: total - success,
            error_rate: Math.round(((total - success) / total) * 10000) / 100,
            success_rate: Math.round((success / total) * 10000) / 100,
        }
    },
})

export const aggregates30D = selector<AggregatesI>({
    key: 'updates_30d_aggregates',
    get: ({ get }) => {
        const values = get(valuesSelector)

        const success = values['success'].slice(-30).reduce((a, b) => a + b, 0)
        const total = values['total'].slice(-30).reduce((a, b) => a + b, 0)

        return {
            total,
            errors: total - success,
            error_rate: Math.round(((total - success) / total) * 10000) / 100,
            success_rate: Math.round((success / total) * 10000) / 100,
        }
    },
})

const get_rate = (item: UpdateStatuses<number>): number => {
    const total =
        item['in progress'] +
        item['request finished'] +
        item['success'] +
        item['failure'] +
        item['created']

    if (total == 0) {
        return 0
    }

    return Math.round((item['failure'] / total) * 10000) / 100
}
